/* ============================================================================
   BRITTEN CUSTOM: Cart 4a bag DRAWER view and add-to-bag interception
   assets/britten-bag-drawer.js. Plain JavaScript, no Liquid.

   Loaded (deferred) after assets/britten-bag.js (the core) on every page EXCEPT the
   cart, when Theme settings > Britten cart > "Use the new grouped bag" is ticked.
   It makes no request until the bag is opened or a product is added, and it adds no
   global fetch or XHR wrapper.

   WHAT IT DOES
   1. Opens sections/britten-bag-drawer.liquid (a right-hand drawer, a bottom sheet
      below 641px) from the header bag icon. The icon stays a link to the bag page
      without this script. Opening re-reads the cart, reconciles it (a custom-length
      veil's missing extra-length charge is added) and re-renders if the screen differs.
      Checkout in the drawer is held (both locks) until that is done.
   2. Dialog behaviour: role=dialog aria-modal, focus moves in and returns to the
      trigger, Tab is trapped, Escape closes, everything behind is inert, the page
      does not scroll and does not shift when the scrollbar goes, reduced motion is
      respected, and the sheet follows the visual viewport (phone keyboards).
   3. With "open the bag drawer after Add to bag" ticked (config drawerAdd), a product
      form that posts to /cart/add is added without leaving the page, then the drawer
      opens on "Added to your bag". See the ADD INTERCEPTION note below.

   ADD INTERCEPTION (britten_cart_4a_drawer_add; off = the page behaves as today)
   - It listens for `submit` on window, in the bubble phase, so every listener on the
     form and on document has already run: the options script (assets/britten-options.js),
     the norefunds hidden input, the length editor's property inputs, the
     pdp_atc_click metric. Nothing is stopped (no stopImmediatePropagation) and no
     button is replaced.
   - If anyone has already called preventDefault on that submit, it is NOT ours: do
     nothing. (That is how the options script stops an add that has a problem: a missing
     required field, say.)
   - Otherwise it prevents the native post and POSTs the same fields once to
     {root}cart/add.js: new FormData(form) with the submitter, so every property and
     hidden input goes as the form holds it. Then it re-reads the cart and runs the
     coordinator's reconcile, and opens the drawer.
   - Fallbacks (assets/britten-bag.js runAddFlow, tested): Shopify refused (4xx) =
     the native submit, once. Network error or 5xx = read the cart first; if the add
     landed, carry on; if not, the native submit, once; if the cart cannot be read,
     nothing is repeated and the drawer opens on the real bag.
   - A second submit while one is in flight is ignored.
   - Product options (assets/britten-options.js): when the form has
     paid extras chosen, BrittenOptions.itemsFor(form, fd) gives the piece and its
     extras as items, and THAT is the one add (JSON), so they land together or not at
     all. If it is refused or does not land, nothing is re-posted natively (that would
     add the piece without its extras): BrittenOptions.addFailed shows a calm message.
     window.BrittenBagDrawer.handlesAdd tells the options script this add is on.
   - Any other form that posts to /cart/add is added the same way, as one add with
     the form's own fields.

   GA4 view_cart (added 19 Oct 2026)
   - Shopify's own cart_viewed event only fires when the /cart PAGE loads. Since the drawer
     went live (6 Oct 2026) almost nobody visits that page, so GA4 view_cart fell to about
     zero. The drawer now publishes `britten_cart_viewed` through Shopify.analytics.publish
     once each time it opens (not on every quantity change, not on a re-render), as soon as
     the cart is known. The "Britten events" custom pixel (docs/veil-guide-custom-pixel.md)
     turns it into a GA4 `view_cart` with value, currency and items. The cart page itself
     never loads this script, so the two can never count the same view twice.

   FAST ADD (theme setting britten_bag_fast_add, config fastAdd; default on)
   - The drawer opens at once, before any request, in a LOADING state: the inline HTML
     is hidden (it is the bag as the page loaded, and on a first add that is an empty
     bag), "Updating your bag..." shows with no delay, and checkout is held.
   - The add POST asks for the drawer section in the same response (assets/britten-bag.js
     addRaw, {bundle:true}). When it comes back the HTML is swapped in at once, "Added to
     your bag" first, and the reconcile runs behind the open drawer, starting from the
     cart it has just read (engine.settleAdd). Checkout stays held until it is done.
   - If the reconcile changes nothing the swapped HTML stays. If it adds the extra-length
     charge (a custom-length veil) "Updating your bag..." stays up and checkout stays
     held until the charge has landed and the drawer has been drawn again.
   - A 4xx on the add closes the loading drawer, then the page's own submit runs, as it
     always did. A network error or a 5xx keeps the "did it land?" checks in runAddFlow;
     the drawer stays loading meanwhile, then shows the bag or closes for the native add.
   - Closing the drawer while it loads is respected: the add finishes quietly and the
     drawer does not reopen.
   - Off: none of this runs. The add is the old sequence, nothing opens until it ends.
   ========================================================================== */
(function (root) {
  'use strict';

  var core = root.BrittenBagCoordinator;
  var document = root.document;
  if (!core || !document) { return; }

  var buildUrl = core.buildUrl;
  var lockControls = core.lockControls;
  var veilUnits = core.veilUnits;
  var displayCount = core.displayCount;
  var createEngine = core.createEngine;
  var createUpdateWriter = core.createUpdateWriter;
  var drawerReducer = core.drawerReducer;
  var runAddFlow = core.runAddFlow;
  var primarySignature = core.primarySignature;
  var i18n = root.BrittenI18n || { t: function () { return ''; } };

  var VEIL_LIMIT = 2;               /* the veil limit's own constant */
  var UPDATING_DELAY_MS = 350;
  var CLOSE_MS = 260;

  var host = null;
  var panel = null;
  var engine = null;
  var cfg = {};
  var rootUrlValue = '/';
  var originValue = '';
  var dstate = drawerReducer(undefined, {});
  var lastTrigger = null;
  var veilHold = false;
  var updatingTimer = null;
  var closeTimer = null;
  var inertSet = [];
  var addBusy = false;
  var addPhase = null;            /* fast add only: null, 'loading' (no HTML yet) or 'verifying' (HTML on screen, reconcile running) */
  var addDismissed = false;       /* she closed the drawer while the add was running */
  var addStatusShown = false;     /* the "Updating your bag..." line is up because of the add, not the 350ms rule */
  var announcedKey;               /* the line already announced as added, so the end does not say it twice */
  var lastVerify = 0;
  var writer = null;
  var noteIntent;                 /* the note as she last typed it this page view */
  var WRITE_DELAY_MS = 600;
  var FLUSH_BOUND_MS = 1500;

  function readConfig() {
    var el = host && host.querySelector('#britten-bag-config');
    if (!el) { return {}; }
    try { return JSON.parse(el.textContent || '{}') || {}; } catch (e) { return {}; }
  }

  function reducedMotion() {
    return !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---- small DOM helpers ---- */

  function q(sel) { return host.querySelector(sel); }
  function groupEls() { return Array.prototype.slice.call(host.querySelectorAll('[data-britten-group]')); }

  function groupByKey(key) {
    var els = groupEls();
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute('data-britten-key') === key) { return els[i]; }
    }
    return null;
  }

  function titleOf(key) {
    var g = groupByKey(key);
    var t = g && g.querySelector('[data-britten-group-title]');
    return t ? t.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function announce(message) {
    var el = q('[data-britten-drawer-live]');
    if (!el || !message) { return; }
    el.textContent = '';
    setTimeout(function () { el.textContent = message; }, 30);
  }

  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) { el.setAttribute(name, value); }
  }

  /* ---- lock (the coordinator's own, plus the veil hold) ---- */

  function isHeld() { return engine.getState().locked || veilHold || addPhase !== null; }

  function applyLock() {
    var locked = isHeld();
    lockControls(host, locked);
    if (!engine.getState().locked && addPhase === null) {
      var markers = host.querySelectorAll('[data-britten-bag-needs-check]');
      for (var i = 0; i < markers.length; i++) { markers[i].parentNode.removeChild(markers[i]); }
    }
  }

  function setVeilHold(cart) {
    if (cart) { veilHold = veilUnits(cart) > VEIL_LIMIT; }
  }

  /* ---- status ---- */

  /* A fast add shows "Updating your bag..." at once, with no delay: while it has no HTML
     yet, and afterwards only while the HTML on screen still says the bag needs checking
     (a custom-length veil whose extra-length charge is about to be added). */
  function addStatusNow() {
    if (addPhase === 'loading') { return true; }
    return addPhase === 'verifying' && !!host.querySelector('[data-britten-bag-needs-check]');
  }

  function paintStatus(s) {
    var el = q('[data-britten-drawer-status]');
    if (!el) { return; }
    var textEl = el.querySelector('[data-britten-drawer-status-text]');
    var noteEl = el.querySelector('[data-britten-drawer-status-note]');
    var actions = el.querySelector('[data-britten-drawer-status-actions]');
    if (s.error) {
      clearTimeout(updatingTimer);
      updatingTimer = null;
      addStatusShown = false;
      el.removeAttribute('hidden');
      el.setAttribute('role', 'alert');
      textEl.textContent = i18n.t('js.bag.error');
      noteEl.textContent = '';
      actions.removeAttribute('hidden');
      return;
    }
    el.removeAttribute('role');
    actions.setAttribute('hidden', '');
    if ((s.locked || addPhase !== null) && dstate.open) {
      if (addStatusNow()) {
        clearTimeout(updatingTimer);
        updatingTimer = null;
        addStatusShown = true;
        textEl.textContent = i18n.t('js.bag.updating');
        noteEl.textContent = i18n.t('js.bag.updating_note');
        el.removeAttribute('hidden');
        return;
      }
      if (addStatusShown) {
        addStatusShown = false;
        el.setAttribute('hidden', '');
      }
      if (!updatingTimer && el.hasAttribute('hidden')) {
        updatingTimer = setTimeout(function () {
          updatingTimer = null;
          var now = engine.getState();
          if (now.locked && !now.error) {
            textEl.textContent = i18n.t('js.bag.updating');
            noteEl.textContent = i18n.t('js.bag.updating_note');
            el.removeAttribute('hidden');
          }
        }, UPDATING_DELAY_MS);
      }
    } else {
      clearTimeout(updatingTimer);
      updatingTimer = null;
      addStatusShown = false;
      el.setAttribute('hidden', '');
    }
  }

  /* Every quantity box reads the last CONFIRMED cart, never a wish (see the bag page's copy). */
  function syncQuantities() {
    if (!engine) { return; }
    /* During a fast add the engine's last confirmed cart is the one read BEFORE the add, so it
       would pull a merged line's quantity back down. The end of the add syncs again. */
    if (addPhase !== null) { return; }
    var cart = engine.confirmedCart();
    if (!cart) { return; }
    var inputs = host.querySelectorAll('[data-britten-qty-input]');
    var list = [];
    var byKey = {};
    for (var i = 0; i < inputs.length; i++) {
      var key = inputs[i].getAttribute('data-britten-key');
      byKey[key] = inputs[i];
      if (document.activeElement === inputs[i]) { continue; }
      list.push({ key: key, value: parseInt(inputs[i].value, 10) });
    }
    var fixes = core.confirmedQuantities(list, cart, engine.intentFor);
    for (var j = 0; j < fixes.length; j++) { byKey[fixes[j].key].value = fixes[j].value; }
  }

  function onState(s) {
    if (s.pending === 0) { syncQuantities(); }
    applyLock();
    var els = groupEls();
    for (var i = 0; i < els.length; i++) {
      var busy = !!s.busy[els[i].getAttribute('data-britten-key')];
      var cur = els[i].getAttribute('aria-busy');
      if (busy && cur !== 'true') { els[i].setAttribute('aria-busy', 'true'); }
      if (!busy && cur !== null) { els[i].removeAttribute('aria-busy'); }
    }
    var body = q('[data-britten-drawer-swap]');
    if (body) {
      if (s.pending > 0) { setAttr(body, 'aria-busy', 'true'); } else if (body.hasAttribute('aria-busy')) { body.removeAttribute('aria-busy'); }
    }
    paintStatus(s);
  }

  /* ---- header count ---- */

  function updateHeaderCount(cart) {
    if (!cart) { return; }
    var count = displayCount(cart);
    var els = document.querySelectorAll('[data-bind="itemCount"]');
    for (var i = 0; i < els.length; i++) { els[i].textContent = count; }
    var trigger = document.querySelector('[data-ajax-cart-trigger]');
    if (trigger) {
      if (count > 0) { trigger.classList.add('has-cart-count'); } else { trigger.classList.remove('has-cart-count'); }
    }
  }

  /* ---- title, ordering ("Added to your bag" first, then "Also in your bag") ---- */

  function paintMode() {
    var bag = q('[data-britten-title-bag]');
    var added = q('[data-britten-title-added]');
    var showAdded = dstate.mode === 'added';
    if (bag) { if (showAdded) { bag.setAttribute('hidden', ''); } else { bag.removeAttribute('hidden'); } }
    if (added) { if (showAdded) { added.removeAttribute('hidden'); } else { added.setAttribute('hidden', ''); } }
  }

  function arrangeAdded() {
    var ul = q('[data-britten-bag-groups]');
    if (!ul) { return; }
    var also = ul.querySelector('[data-britten-also]');
    var groups = groupEls();
    var i;
    for (i = 0; i < groups.length; i++) { groups[i].removeAttribute('data-britten-added'); }
    if (dstate.mode === 'added' && dstate.addedKey) {
      var g = groupByKey(dstate.addedKey);
      if (!g) {
        dstate = drawerReducer(dstate, { type: 'added-gone' });
        paintMode();
      } else {
        g.setAttribute('data-britten-added', '');
        ul.insertBefore(g, ul.firstChild);
        if (also) {
          ul.insertBefore(also, g.nextSibling);
          if (groups.length > 1) { also.removeAttribute('hidden'); } else { also.setAttribute('hidden', ''); }
        }
        return;
      }
    }
    if (also) {
      also.setAttribute('hidden', '');
      ul.insertBefore(also, ul.firstChild);
    }
  }

  /* ---- section swap, focus ---- */

  function focusSnapshot() {
    var a = document.activeElement;
    if (!a || !host.contains(a)) { return null; }
    var el = a.closest ? a.closest('[data-britten-focus]') : null;
    if (!el) { return null; }
    var snap = { id: el.getAttribute('data-britten-focus'), index: -1 };
    var group = el.closest('[data-britten-group]');
    if (group) { snap.index = groupEls().indexOf(group); }
    return snap;
  }

  function restoreFocus(snap) {
    if (!snap) { return; }
    var els = host.querySelectorAll('[data-britten-focus]');
    var i;
    for (i = 0; i < els.length; i++) {
      if (els[i].getAttribute('data-britten-focus') === snap.id) { els[i].focus({ preventScroll: true }); return; }
    }
    var groups = groupEls();
    var target = null;
    if (groups.length) {
      target = groups[Math.min(Math.max(snap.index, 0), groups.length - 1)].querySelector('.britten-bag-group__title a');
    }
    if (!target) { target = q('[data-britten-drawer-title]'); }
    if (target) { target.focus({ preventScroll: true }); }
  }

  function reapplyIntent() {
    var inputs = host.querySelectorAll('[data-britten-qty-input]');
    for (var i = 0; i < inputs.length; i++) {
      var want = engine.intentFor(inputs[i].getAttribute('data-britten-key'));
      if (want !== undefined) { inputs[i].value = want; }
    }
  }

  function domSignature() {
    var els = groupEls();
    var out = [];
    for (var i = 0; i < els.length; i++) {
      var input = els[i].querySelector('[data-britten-qty-input]');
      out.push(els[i].getAttribute('data-britten-key') + ':' + (input ? parseInt(input.defaultValue, 10) : 0));
    }
    return out.sort().join('|');
  }

  /* ---- workshop note (same data path as the bag page: cart/update.js {note}) ---- */

  function sendUpdate(body) {
    try {
      var p = root.fetch(buildUrl('/', 'cart/update.js', originValue), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify(body)
      });
      return (p && typeof p.then === 'function') ? p.then(function () {}, function () {}) : Promise.resolve();
    } catch (e) { return Promise.resolve(); }
  }

  function writeNote(value) {
    if (!writer) { writer = createUpdateWriter(sendUpdate, WRITE_DELAY_MS); }
    writer.schedule({ note: value });
  }

  function noteField() { return host.querySelector('[data-britten-drawer-note]'); }

  function noteSnapshot() {
    var ta = noteField();
    if (!ta) { return null; }
    var toggle = host.querySelector('[data-britten-act="notes"]');
    var snap = { expanded: !!(toggle && toggle.getAttribute('aria-expanded') === 'true'), focus: document.activeElement === ta };
    if (snap.focus) { try { snap.start = ta.selectionStart; snap.end = ta.selectionEnd; } catch (e) { /* no selection */ } }
    return snap;
  }

  function showClear() {
    var ta = noteField();
    var btns = host.querySelectorAll('[data-britten-act="clear-note"]');
    for (var i = 0; i < btns.length; i++) {
      if (ta) { if (ta.value) { btns[i].removeAttribute('hidden'); } else { btns[i].setAttribute('hidden', ''); } }
    }
  }

  /* "Clear note": empty the box, save note "" at once through the same serialised writer, say
     so, keep focus in the box, hide the button. Also works from the empty-bag preview. */
  function clearNote() {
    noteIntent = '';
    var ta = noteField();
    if (ta) { ta.value = ''; ta.focus(); }
    writeNote('');
    writer.flush();
    var prev = host.querySelector('[data-britten-note-preview]');
    if (prev && prev.parentNode) { prev.parentNode.removeChild(prev); }
    showClear();
    announce(i18n.t('js.bag.announce_note_cleared'));
  }

  function restoreNote(snap) {
    var ta = noteField();
    if (!ta || !snap) { return; }
    if (noteIntent !== undefined) { ta.value = noteIntent; }
    showClear();
    var toggle = host.querySelector('[data-britten-act="notes"]');
    var field = host.querySelector('#britten-bagd-notes-field');
    if (snap.expanded || ta.value) {
      if (toggle) { toggle.setAttribute('aria-expanded', 'true'); }
      if (field) { field.removeAttribute('hidden'); }
    }
    if (snap.focus) {
      ta.focus({ preventScroll: true });
      try { if (typeof snap.start === 'number') { ta.setSelectionRange(snap.start, snap.end); } } catch (e) { /* no selection */ }
    }
  }

  /* Secure Checkout: send any note still waiting, then submit. Bounded, so a slow network can
     never trap her. The textarea is also in the form, so the post carries the note regardless. */
  function flushThenCheckout(btn) {
    var done = false;
    function go() {
      if (done) { return; }
      done = true;
      var form = host.querySelector('[data-britten-drawer-form]');
      if (form && typeof form.requestSubmit === 'function') { form.requestSubmit(btn); } else { btn.click(); }
    }
    var bound = setTimeout(go, FLUSH_BOUND_MS);
    writer.flush().then(function () { clearTimeout(bound); go(); });
  }

  function swapDrawer(html, cart) {
    var doc;
    try { doc = new root.DOMParser().parseFromString(html, 'text/html'); } catch (e) { doc = null; }
    var fresh = doc && doc.querySelector('[data-britten-drawer-swap]');
    var live = q('[data-britten-drawer-swap]');
    if (!fresh || !live) {
      var err = new Error('render');
      err.code = 'render';
      throw err;
    }
    var scroll = q('[data-britten-drawer-scroll]');
    var top = scroll ? scroll.scrollTop : 0;
    var snap = focusSnapshot();
    var noteSnap = noteSnapshot();
    live.innerHTML = fresh.innerHTML;
    if (host.hasAttribute('data-britten-loading')) { host.removeAttribute('data-britten-loading'); }
    restoreNote(noteSnap);
    setVeilHold(cart);
    arrangeAdded();
    paintMode();
    applyLock();
    onState(engine.getState());
    updateHeaderCount(cart);
    reapplyIntent();
    var scrollNow = q('[data-britten-drawer-scroll]');
    if (scrollNow && dstate.mode !== 'added') { scrollNow.scrollTop = top; }
    restoreFocus(snap);
  }

  /* ---- open / close: dialog behaviour ---- */

  function focusables() {
    var list = panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
    var out = [];
    var loading = host.hasAttribute('data-britten-loading');   /* the body is hidden while an add loads */
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.disabled || el.hasAttribute('hidden') || el.closest('[hidden]') || el.closest('[inert]')) { continue; }
      if (loading && el.closest('[data-britten-drawer-swap]')) { continue; }
      if (el.getAttribute('tabindex') === '-1') { continue; }
      if (el.getAttribute('aria-hidden') === 'true') { continue; }
      out.push(el);
    }
    return out;
  }

  function setInertBehind(on) {
    var i;
    if (!on) {
      for (i = 0; i < inertSet.length; i++) { inertSet[i].removeAttribute('inert'); }
      inertSet = [];
      return;
    }
    var mine = host.closest('.shopify-section') || host;
    var kids = document.body.children;
    for (i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === mine || el.contains(mine)) { continue; }
      if (/^(SCRIPT|STYLE|LINK|NOSCRIPT|TEMPLATE)$/.test(el.tagName)) { continue; }
      if (el.hasAttribute('inert')) { continue; }
      el.setAttribute('inert', '');
      inertSet.push(el);
    }
  }

  function lockScroll(on) {
    var html = document.documentElement;
    if (on) {
      var gap = root.innerWidth - html.clientWidth;
      html.style.setProperty('--britten-bagd-sbw', (gap > 0 ? gap : 0) + 'px');
      html.classList.add('britten-bagd-lock');
    } else {
      html.classList.remove('britten-bagd-lock');
      html.style.removeProperty('--britten-bagd-sbw');
    }
  }

  /* The header's own layers (menu, search, location, Saved) close on Escape. Send
     it before opening so only one overlay owns focus and scrolling. The drawer's
     own Escape handler is not live yet, so this cannot close the drawer. */
  function closeHeaderLayers() {
    try {
      for (var i = 0; i < 4; i++) {
        document.dispatchEvent(new root.KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      }
    } catch (e) { /* no layers to close */ }
  }

  function followViewport() {
    if (!root.visualViewport) { return; }
    var apply = function () {
      if (dstate.open) { host.style.setProperty('--britten-bagd-vh', root.visualViewport.height + 'px'); }
    };
    root.visualViewport.addEventListener('resize', apply);
    root.visualViewport.addEventListener('scroll', apply);
    apply();
  }

  /* GA4 view_cart: one `britten_cart_viewed` per drawer open, sent once the cart is known.
     viewPending is set when the drawer goes from closed to open and cleared when it is sent
     or the drawer closes first. Prices in /cart.js are in pence. */
  var viewPending = false;

  function cartViewedPayload(cart) {
    var items = [];
    var lines = (cart && cart.items) || [];
    for (var i = 0; i < lines.length && i < 10; i++) {
      var l = lines[i];
      items.push({
        item_id: String(l.product_id || ''),
        item_variant: String(l.variant_id || ''),
        item_name: String(l.product_title || l.title || '').slice(0, 100),
        price: (Number(l.final_price != null ? l.final_price : l.price) || 0) / 100,
        quantity: Number(l.quantity) || 0
      });
    }
    return {
      currency: String((cart && cart.currency) || ''),
      value: (Number(cart && cart.total_price) || 0) / 100,
      item_count: Number(cart && cart.item_count) || 0,
      items: items
    };
  }

  function trackCartViewed(cart) {
    if (!viewPending || !cart || !dstate.open) { return; }
    viewPending = false;
    try {
      var sh = root.Shopify;
      if (sh && sh.analytics && typeof sh.analytics.publish === 'function') {
        sh.analytics.publish('britten_cart_viewed', cartViewedPayload(cart));
      }
    } catch (err) { /* analytics must never break the bag */ }
  }

  /* opts (fast add only): loading  open without reading the cart, the add is about to run
                            settle   the add has finished: do not take focus or announce again
                            verify   read the cart and re-render if the screen differs, even
                                     though the drawer was already open */
  function open(mode, key, trigger, cart, opts) {
    if (!host) { return; }
    opts = opts || {};
    var wasOpen = dstate.open;
    dstate = drawerReducer(dstate, mode === 'added' ? { type: 'open-added', key: key } : { type: 'open-bag' });
    clearTimeout(closeTimer);
    if (!wasOpen) {
      viewPending = true;
      addDismissed = false;
      lastTrigger = trigger || document.activeElement;
      closeHeaderLayers();
      lockScroll(true);
      setInertBehind(true);
      host.removeAttribute('hidden');
      host.style.setProperty('--britten-bagd-vh', (root.visualViewport ? root.visualViewport.height : root.innerHeight) + 'px');
      void host.offsetWidth;                      /* so the transition runs */
      host.classList.add('britten-bagd--open');
    }
    if (cart) { setVeilHold(cart); updateHeaderCount(cart); trackCartViewed(cart); }
    arrangeAdded();
    paintMode();
    applyLock();
    var title = q('[data-britten-drawer-title]');
    if (title && !opts.settle) { title.focus({ preventScroll: true }); }
    if (mode === 'added' && key && !(opts.settle && announcedKey === key)) {
      announcedKey = key;
      announce(i18n.t('js.bag.announce_added', { product: titleOf(key) }));
    }
    if (((!wasOpen && mode !== 'added') || opts.verify) && !opts.loading) {
      /* Read the cart, reconcile it, and re-render if the screen is out of date.
         Checkout stays held while this runs. */
      lastVerify = Date.now();
      engine.verify();
    }
  }

  function close(immediate) {
    if (!dstate.open) { return; }
    viewPending = false;
    if (addPhase !== null) { addDismissed = true; }   /* she closed it on purpose while the add ran */
    dstate = drawerReducer(dstate, { type: 'close' });
    if (writer) { writer.flush(); }                 /* a note typed just now is saved, not lost */
    host.classList.remove('britten-bagd--open');
    setInertBehind(false);
    lockScroll(false);
    var finish = function () {
      if (!dstate.open) { host.setAttribute('hidden', ''); }
    };
    clearTimeout(closeTimer);
    if (immediate || reducedMotion()) { finish(); } else { closeTimer = setTimeout(finish, CLOSE_MS); }
    paintMode();
    var back = lastTrigger;
    lastTrigger = null;
    if (!back || !document.contains(back) || back === document.body) { back = document.querySelector('a.bh-bag, [data-ajax-cart-trigger]'); }
    if (back && typeof back.focus === 'function') { back.focus({ preventScroll: true }); }
  }

  /* ---- events ---- */

  function quantityInputFor(groupEl) {
    return groupEl ? groupEl.querySelector('[data-britten-qty-input]') : null;
  }

  function announceFor(res, kind, title) {
    if (!res || !res.ok) { return; }
    if (kind === 'remove') { announce(i18n.t('js.bag.announce_removed', { product: title })); return; }
    announce(i18n.t('js.bag.announce_updated'));
  }

  function changeQuantity(key, next) {
    var title = titleOf(key);
    var removing = next <= 0;
    engine.setQuantity(key, next).then(function (res) { announceFor(res, removing ? 'remove' : 'update', title); });
  }

  function commitInput(input) {
    var key = input.getAttribute('data-britten-key');
    var n = parseInt(input.value, 10);
    if (isNaN(n) || n < 0) { n = 0; }
    input.value = n;
    changeQuantity(key, n);
  }

  function onClick(e) {
    var t = e.target;
    if (!t || !t.closest) { return; }
    if (t.closest('[data-britten-drawer-close]')) { e.preventDefault(); close(); return; }
    var checkout = t.closest('button[name="checkout"]');
    if (checkout && isHeld()) { e.preventDefault(); e.stopPropagation(); return; }
    if (checkout && writer && writer.hasWork()) {
      e.preventDefault();
      e.stopPropagation();
      flushThenCheckout(checkout);
      return;
    }
    var el = t.closest('[data-britten-act]');
    if (!el || !host.contains(el)) { return; }
    var act = el.getAttribute('data-britten-act');
    var key = el.getAttribute('data-britten-key');
    var input = quantityInputFor(key ? groupByKey(key) : null);
    if (act === 'inc' || act === 'dec') {
      e.preventDefault();
      if (!input) { return; }
      var wanted = engine.intentFor(key);
      var current = wanted !== undefined ? wanted : (parseInt(input.value, 10) || 0);
      var next = act === 'inc' ? current + 1 : Math.max(0, current - 1);
      input.value = next;
      changeQuantity(key, next);
    } else if (act === 'remove') {
      e.preventDefault();
      var title = titleOf(key);
      engine.removeGroup(key).then(function (res) { announceFor(res, 'remove', title); });
    } else if (act === 'retry') {
      e.preventDefault();
      engine.retry().then(function (res) { announceFor(res, 'update'); });
    } else if (act === 'clear-note') {
      e.preventDefault();
      clearNote();
    } else if (act === 'notes') {
      e.preventDefault();
      var field = host.querySelector('#britten-bagd-notes-field');
      var isOpen = el.getAttribute('aria-expanded') === 'true';
      el.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      if (field) {
        if (isOpen) { field.setAttribute('hidden', ''); } else {
          field.removeAttribute('hidden');
          var ta = field.querySelector('textarea');
          if (ta) { ta.focus(); }
        }
      }
    }
  }

  function onInput(e) {
    var t = e.target;
    if (t && t.hasAttribute && t.hasAttribute('data-britten-drawer-note')) {
      noteIntent = t.value;
      writeNote(t.value);                           /* exactly what she typed: no trimming */
      showClear();
    }
  }

  function onFocusOut(e) {
    var t = e.target;
    if (t && t.hasAttribute && t.hasAttribute('data-britten-drawer-note') && writer) { writer.flush(); }
    if (host && t && t.hasAttribute && t.hasAttribute('data-britten-drawer-note')) { host.removeAttribute('data-britten-note-focus'); }
  }

  function onFocusIn(e) {
    var t = e.target;
    if (t && t.hasAttribute && t.hasAttribute('data-britten-drawer-note')) { host.setAttribute('data-britten-note-focus', ''); }
  }

  function onChange(e) {
    var t = e.target;
    if (t && t.hasAttribute && t.hasAttribute('data-britten-qty-input')) { commitInput(t); }
  }

  function onKeydown(e) {
    if (!dstate.open) { return; }
    if (e.key === 'Escape' || e.key === 'Esc') {
      if (!e.isTrusted && e.target === document) { return; }
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Enter' && e.target && e.target.hasAttribute && e.target.hasAttribute('data-britten-qty-input')) {
      e.preventDefault();
      commitInput(e.target);
      return;
    }
    if (e.key === 'Tab') {
      var list = focusables();
      if (!list.length) { e.preventDefault(); panel.focus(); return; }
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || !panel.contains(active))) { e.preventDefault(); first.focus(); }
    }
  }

  function onSubmitInDrawer(e) {
    if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-britten-drawer-form') && isHeld()) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  function onBagIconClick(e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { return; }
    var a = e.target && e.target.closest ? e.target.closest('a.bh-bag, a[data-ajax-cart-trigger]') : null;
    if (!a) { return; }
    e.preventDefault();
    open('bag', null, a);
  }

  /* ---- the add-to-bag interception ---- */

  function formEntries(form, submitter) {
    var fd;
    try { fd = submitter ? new root.FormData(form, submitter) : new root.FormData(form); } catch (err) { fd = new root.FormData(form); }
    return fd;
  }

  /* The button focus goes back to when the drawer closes: the real submitter, or the form's
     own add button. Never the <form> (it cannot take focus). */
  function addButtonFor(form, submitter) {
    if (submitter && submitter.isConnected !== false && typeof submitter.focus === 'function') { return submitter; }
    return form.querySelector('[data-add-to-cart-trigger], button.button--add-to-cart, button[name="add"], [type="submit"]') || null;
  }

  /* ---- fast add: the loading drawer ---- */

  /* The drawer's own HTML, if it came back with the add. */
  function sectionHtmlFrom(r) {
    var sections = r && r.data && r.data.sections;
    var html = sections && sections[cfg.sectionId || 'britten-bag-drawer'];
    return (typeof html === 'string' && html) ? html : '';
  }

  /* The line the add made, from Shopify's own answer. Only used to put "Added to your bag"
     first straight away; the end of the add re-checks it against the cart (findAddedKey). */
  function addedKeyFrom(r) {
    var d = r && r.data;
    if (!d) { return null; }
    if (typeof d.key === 'string' && d.key) { return d.key; }
    var first = d.items && d.items[0];
    return (first && typeof first.key === 'string' && first.key) ? first.key : null;
  }

  /* Leave the add state. Callers repaint the lock and status afterwards. */
  function endAddUi() {
    addPhase = null;
    addStatusShown = false;
    if (host.hasAttribute('data-britten-loading')) { host.removeAttribute('data-britten-loading'); }
  }

  /* Open at once, before any request. The inline HTML is the bag as the page loaded (on a
     first add, an empty bag), so it stays hidden until the real HTML replaces it. */
  function beginAdd(trigger) {
    addPhase = 'loading';
    addDismissed = false;
    addStatusShown = false;
    announcedKey = undefined;
    host.setAttribute('data-britten-loading', '');
    open('bag', null, trigger, null, { loading: true });
    paintStatus(engine.getState());
  }

  /* The add was accepted and the drawer HTML came with it: draw it now, "Added to your bag"
     first, and keep checkout held while the reconcile runs behind it. True if it is on
     screen; false leaves the usual render (the section fetched after the reconcile). */
  function showAddedHtml(r) {
    var html = sectionHtmlFrom(r);
    if (!html || addPhase === null) { return false; }
    var key = addedKeyFrom(r);
    if (dstate.open) { dstate = drawerReducer(dstate, { type: 'open-added', key: key }); }
    swapDrawer(html, null);
    addPhase = 'verifying';
    applyLock();
    paintStatus(engine.getState());
    if (dstate.open && dstate.mode === 'added' && dstate.addedKey) {
      announcedKey = dstate.addedKey;
      announce(i18n.t('js.bag.announce_added', { product: titleOf(dstate.addedKey) }));
    }
    return true;
  }

  /* The add did not go through here (a refusal, or it did not land): close the loading
     drawer so the page's own add can run exactly as it always did. */
  function abandonAdd() {
    endAddUi();
    if (dstate.open) { close(true); }
    applyLock();
    paintStatus(engine.getState());
  }

  /* The add is over. If she closed the drawer meanwhile it stays closed. */
  function finishAdd(mode, key, cart, trigger) {
    var dismissed = addDismissed;
    endAddUi();
    addDismissed = false;
    if (dismissed || !dstate.open) {
      if (cart) { setVeilHold(cart); updateHeaderCount(cart); }
      applyLock();
      onState(engine.getState());
      return;
    }
    if (mode === 'added') {
      open('added', key, trigger, cart, { settle: true });
    } else {
      open('bag', null, trigger, cart, { verify: true });     /* unsure: read the real bag */
    }
    onState(engine.getState());
  }

  function onProductSubmit(e) {
    if (!cfg.drawerAdd || !engine) { return; }
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') { return; }
    if (e.defaultPrevented) { return; }             /* someone else (the options script) owns this submit */
    var action = (form.getAttribute('action') || '').split('?')[0];
    if (!/\/cart\/add\/?$/.test(action)) { return; }
    if (!form.querySelector('[name="id"]')) { return; }
    if (host.contains(form)) { return; }

    var submitter = e.submitter || null;
    var fd = formEntries(form, submitter);
    /* Our own product options (assets/britten-options.js): a form with paid extras
       chosen is added as ONE request of items (the piece, then its extras), so they
       all land or none do. If that add fails nothing is re-posted: a plain post
       would add the piece without its extras. */
    var optionItems = null;
    try {
      optionItems = (root.BrittenOptions && typeof root.BrittenOptions.itemsFor === 'function') ? root.BrittenOptions.itemsFor(form, fd) : null;
    } catch (err) { optionItems = null; }

    e.preventDefault();                               /* synchronous: the native post must not start */
    if (addBusy) { return; }                          /* a second tap while one add is in flight */
    addBusy = true;

    var focusTarget = addButtonFor(form, submitter);
    var fast = cfg.fastAdd === true;                  /* britten_bag_fast_add; anything else is the old sequence */
    var busyEl = submitter;
    var rendered = false;                             /* fast add: the drawer HTML that came with the add is on screen */
    if (fast) {
      busyEl = focusTarget;
      try { beginAdd(focusTarget); } catch (err) {
        fast = false;                                 /* could not open: carry on with the old sequence */
        try { abandonAdd(); } catch (err2) { endAddUi(); }
      }
    }
    if (busyEl) { busyEl.setAttribute('aria-busy', 'true'); }
    var nativeUsed = false;

    var deps = {
      readCart: function () { return engine.readCart(); },
      postAdd: function () { return engine.addRaw(fd); },
      afterAdd: function () { return engine.refresh(); },
      openAdded: function (key, cart) { open('added', key, focusTarget, cart); },
      openBag: function () { open('bag', null, focusTarget); },
      nativeSubmit: function () {
        if (nativeUsed) { return; }
        nativeUsed = true;
        if (optionItems) {                             /* never a plain re-post of a piece with extras */
          try { root.BrittenOptions.addFailed(form); } catch (err) { /* message only */ }
          return;
        }
        HTMLFormElement.prototype.submit.call(form);   /* the page's own add, exactly as it would have gone */
      }
    };
    var addBody = optionItems ? { items: optionItems } : fd;
    deps.postAdd = function () { return engine.addRaw(addBody); };
    if (fast) {
      deps.fast = true;
      deps.postAdd = function () { return engine.addRaw(addBody, { bundle: true }); };
      deps.onAdded = function (r) {
        try { rendered = showAddedHtml(r); } catch (err) { rendered = false; }
      };
      deps.afterAdd = function (after) {
        /* the cart just read after the add is the reconcile's first pass; with none, the old refresh */
        return after ? engine.settleAdd(after, { rendered: rendered }) : engine.refresh();
      };
      deps.abandon = abandonAdd;
      /* A drawing error after a good add must never turn into a second, native add. */
      deps.openAdded = function (key, cart) {
        try { finishAdd('added', key, cart, focusTarget); } catch (err) { endAddUi(); applyLock(); }
      };
      deps.openBag = function () {
        try { finishAdd('bag', null, null, focusTarget); } catch (err) { endAddUi(); applyLock(); }
      };
    }

    function over() {
      addBusy = false;
      if (busyEl) { busyEl.removeAttribute('aria-busy'); }
    }

    runAddFlow(deps).then(function () {
      over();
      if (fast && addPhase !== null) { endAddUi(); applyLock(); paintStatus(engine.getState()); }
    }, function () {
      over();
      if (fast) { try { abandonAdd(); } catch (err) { endAddUi(); } }
      if (!nativeUsed) { deps.nativeSubmit(); }
    });
  }

  /* ---- boot ---- */

  function init() {
    host = document.querySelector('[data-britten-drawer]');
    if (!host) { return; }
    panel = host.querySelector('[data-britten-drawer-panel]');
    cfg = readConfig();
    var shopifyRoot = root.Shopify && root.Shopify.routes && root.Shopify.routes.root_url;
    rootUrlValue = shopifyRoot || cfg.rootUrl || '/';
    originValue = root.location ? root.location.origin : '';

    engine = createEngine({
      fetch: function (url, init) { return root.fetch(url, init); },
      rootUrl: rootUrlValue,
      sectionId: cfg.sectionId || 'britten-bag-drawer',
      sectionsUrl: root.location ? root.location.pathname : '',
      origin: originValue,
      hooks: {
        onState: onState,
        render: swapDrawer,
        signature: domSignature,
        signatureOf: primarySignature,
        onSettled: function (info) {
          if (info.cart) { setVeilHold(info.cart); updateHeaderCount(info.cart); trackCartViewed(info.cart); }
          if (info.mutated) {
            try {
              document.dispatchEvent(new root.CustomEvent('britten:cart:updated', { bubbles: true, detail: { source: 'britten-bag', cart: info.cart } }));
            } catch (err) { /* old browsers: no event */ }
          }
        },
        onFailure: function (err, meta) {
          if (meta && meta.key) {
            var input = quantityInputFor(groupByKey(meta.key));
            if (input) { input.value = input.defaultValue; }
          }
        }
      }
    });
    if (typeof root.__brittenVeilLimitInitialCount === 'number') { veilHold = root.__brittenVeilLimitInitialCount > VEIL_LIMIT; }

    host.addEventListener('click', onClick, true);
    host.addEventListener('change', onChange);
    host.addEventListener('input', onInput);
    host.addEventListener('focusin', onFocusIn);
    host.addEventListener('focusout', onFocusOut);
    host.addEventListener('submit', onSubmitInDrawer, true);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onBagIconClick);
    root.addEventListener('submit', onProductSubmit);
    /* assets/britten-options.js leaves a form with paid extras to this add when it is on. */
    root.BrittenBagDrawer = root.BrittenBagDrawer || {};
    root.BrittenBagDrawer.handlesAdd = cfg.drawerAdd === true;

    /* Another script (the veil limit) may lift disabled/inert while the coordinator holds the lock. */
    if (typeof root.MutationObserver !== 'undefined') {
      new root.MutationObserver(function () { if (isHeld()) { applyLock(); } }).observe(host, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'inert', 'data-britten-bag-locked']
      });
    }

    /* Back/forward cache: the page can return with the drawer open and an old bag. */
    root.addEventListener('pageshow', function (e) {
      if (!e.persisted) { return; }
      if (addPhase !== null) { endAddUi(); }          /* an add cut off by leaving the page must not hold checkout */
      close(true);
      engine.verify();
    });

    followViewport();
    paintMode();
    onState(engine.getState());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
